// The progress store: a vanilla zustand store, deliberately outside React.
//
// Outside React because the sync engine subscribes to it rather than being
// called from the render tree — an effect that pushes on every commit is how
// a sync layer ends up coupled to rendering and impossible to test.
//
// NO `persist` middleware. Its version semantics are "migrate forward or
// wipe", which the stale-version guard forbids remotely, and it expresses neither
// per-user keying nor one-shot legacy adoption nor fold-on-pull. The vanilla
// store plus local.ts is the right shape and is a dozen lines.

import type { EventId } from "@aybuben/core/progress/clock.ts";
import {
	makeContext,
	type EventContext,
	type ProgressEvent,
} from "@aybuben/core/progress/event.ts";
import { foldKey } from "@aybuben/core/progress/fold-key.ts";
import { applyEvent } from "@aybuben/core/progress/reduce.ts";
import type { GameState } from "@aybuben/core/state.ts";
import { defaultState } from "@aybuben/core/storage.ts";
import { createStore, type StoreApi } from "zustand/vanilla";

import { deviceId } from "./device.ts";
import { loadLocal, saveLocal, type LocalProgress } from "./local.ts";

export type SyncStatus = "idle" | "pushing" | "dirty" | "stale";

interface ProgressState {
	userId: string;
	/** The `foldKey` this store's snapshot is valid under: content version and fold rules. */
	content: string;
	/** Minting context for this device. The engine reads its clock to observe pulled stamps. */
	ctx: EventContext;
	/**
	 * The fold result — the only thing the UI reads.
	 *
	 * Never assigned from outside. `record` computes it with applyEvent, and
	 * `adopt` takes a value that came out of `fold`. That is what makes it
	 * impossible for the UI to construct a state the fold could not have
	 * produced, which is the defect class this whole store exists to remove.
	 */
	state: GameState;
	/** Recorded, not yet pushed. */
	tail: ProgressEvent[];
	/** Server watermark: the event row id `state` folds through. */
	cursor: string | null;
	sync: SyncStatus;

	record(e: ProgressEvent): void;
	/** Engine only. `state` must be a `fold` result; `tail` what is still unpushed. */
	adopt(state: GameState, tail: ProgressEvent[], cursor: string | null): void;
	/** Engine only. Drops the events a push confirmed and advances the watermark. */
	drain(ids: readonly EventId[], cursor: string | null): void;
	setSync(s: SyncStatus): void;
}

export type ProgressStore = StoreApi<ProgressState>;

export interface ProgressStoreOptions {
	userId: string;
	/** The content version. The store keys its snapshots on `foldKey` of it. */
	content: string;
	/** Injectable for tests; one per device in the app. */
	ctx?: EventContext;
	load?: (userId: string, content: string) => LocalProgress | null;
	save?: (userId: string, p: LocalProgress) => void;
}

/**
 * Called once per (user id, content version) pair, and held in a ref. Never in
 * a default parameter, never inline in render: a per-render store would split
 * the two holders of it apart, because the engine captures its store at
 * construction and subscribes to that instance for its lifetime, and would reset
 * `sync` to "idle" on every render, since sync status is not part of the
 * persisted payload.
 */
export function createProgressStore(o: ProgressStoreOptions): ProgressStore {
	const load = o.load ?? loadLocal;
	const save = o.save ?? saveLocal;
	const ctx = o.ctx ?? makeContext(deviceId());
	// Composed here rather than by the caller, so no store can be built that
	// trusts a snapshot folded under other rules.
	const key = foldKey(o.content);
	const stored = load(o.userId, key);

	return createStore<ProgressState>()((set, get) => {
		const persist = (): void => {
			const s = get();
			save(s.userId, {
				version: 6,
				content: s.content,
				snapshot: s.state,
				coversUpTo: s.cursor,
				tail: s.tail,
			});
		};

		return {
			userId: o.userId,
			content: key,
			ctx,
			// Hydrated synchronously, so the first render of whatever consumes this
			// store already shows real progress rather than a default state that a
			// frame later gets replaced.
			state: stored?.snapshot ?? defaultState(),
			tail: stored?.tail ?? [],
			cursor: stored?.coversUpTo ?? null,
			sync: "idle",

			record(e) {
				const s = get();
				set({
					state: applyEvent(s.state, e),
					tail: [...s.tail, e],
					// A stale store keeps refusing to sync until the build is reloaded
					//; recording must not quietly clear that.
					sync: s.sync === "stale" ? "stale" : "dirty",
				});
				// Durability comes from here, synchronously, before any network
				// exists. Everything the engine does afterwards is an optimization.
				persist();
			},

			adopt(state, tail, cursor) {
				set({ state, tail, cursor });
				persist();
			},

			drain(ids, cursor) {
				const gone = new Set(ids);
				const s = get();
				set({ tail: s.tail.filter((e) => !gone.has(e.id)), cursor });
				persist();
			},

			setSync(sync) {
				set({ sync });
			},
		};
	});
}
